import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Checkbox,
  Chip,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useDispatch, useSelector } from "react-redux";
import HeaderBannerPage from "../components/HeaderBannerPage";
import {
  fetchLeavePriorList,
  updateLeavePriorList,
} from "../actions/LeavePriorAction";

/* -------------------- Styles -------------------- */
const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "16px",
  boxShadow: "0px 6px 15px rgba(0,0,0,0.12)",
  border: "1px solid #d6dce5",
  backgroundColor: "#ffffff",
  maxHeight: "600px",
  overflow: "auto",
  width: "100%",
}));

const StyledTableCell = styled(TableCell)(() => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "10px 8px",
  fontSize: "13px",
  textAlign: "center",
  whiteSpace: "nowrap",
  "&.MuiTableCell-head": {
    backgroundColor: "#154c79",
    color: "#ffffff",
    fontWeight: 600,
    borderBottom: "none",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "0.25s ease",
  "&:nth-of-type(odd)": { backgroundColor: "rgba(243, 246, 250, 0.6)" },
  "&:hover": { backgroundColor: "rgba(14, 60, 102, 0.09)", cursor: "pointer" },
}));

// Local yyyy-MM-dd (toISOString shifts to UTC and can give the previous day in Sri Lanka)
const formatDate = (dateObj) => {
  const d = new Date(dateObj);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const isApproved = (item) => String(item.Status || "").toUpperCase() === "Y";


const FILTER_BY_LOCATION = true;

// D / E category -> all DPM division leaves (all locations)
const DIVISION_VIEW_CATEGORIES = ["D", "E"];
const DIVISION_VIEW_DIVISION = "DPM";

// Logged user's category — 5th part of Login responceMessage "empId/name/dept/location/CATEGORY/subCategory"
const getLoggedCategory = () => {
  let cat = localStorage.getItem("employeeCategory") || "";
  if (!cat) {
    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "null");
      cat = String(userData?.responceMessage || "").split("/")[4] || "";
    } catch (e) {
      cat = "";
    }
  }
  return cat.trim().toUpperCase();
};

const getLoggedLocation = () => {
  let loc = localStorage.getItem("role") || "";
  if (!loc) {
    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "null");
      loc = String(userData?.responceMessage || "").split("/")[3] || "";
    } catch (e) {
      loc = "";
    }
  }
  return loc.trim().toUpperCase();
};

/* -------------------- Component -------------------- */
const LeaveApproval = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    leavePriorList,
    leavePriorListLoading,
    leavePriorListError,
    updateLoading,
  } = useSelector((state) => state.leavePrior);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [search, setSearch] = useState("");
  const [remarks, setRemarks] = useState("Approved");
  const [selected, setSelected] = useState([]); // BarcodeNo list
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  const loggedLocation = getLoggedLocation();
  const isDivisionView = DIVISION_VIEW_CATEGORIES.includes(getLoggedCategory());

  // D / E -> all DPM division employees, others -> logged user's location only
  const locationList = useMemo(() => {
    const list = leavePriorList || [];
    if (!FILTER_BY_LOCATION) return list;
    if (isDivisionView) {
      return list.filter(
        (item) => String(item.Division || "").trim().toUpperCase() === DIVISION_VIEW_DIVISION
      );
    }
    return list.filter(
      (item) => String(item.Location || "").trim().toUpperCase() === loggedLocation
    );
  }, [leavePriorList, loggedLocation, isDivisionView]);

  const loadList = () => {
    setSelected([]);
    dispatch(fetchLeavePriorList(formatDate(selectedDate)));
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedDate]);

  /* -------------------- Filter -------------------- */
  // Hide already approved employees (Status "Y")
  const pendingList = useMemo(
    () => locationList.filter((item) => !isApproved(item)),
    [locationList]
  );

  const filteredList = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pendingList;
    return pendingList.filter((item) =>
      [item.ServiceNo, item.Name, item.BarcodeNo, item.Location, item.Department, item.Designation]
        .some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [pendingList, search]);

  // Only not-yet-approved rows can be ticked
  const selectableRows = filteredList.filter((item) => !isApproved(item));
  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every((item) => selected.includes(item.BarcodeNo));
  const someSelected = selectableRows.some((item) => selected.includes(item.BarcodeNo));

  const handleToggle = (barcodeNo) => {
    setSelected((prev) =>
      prev.includes(barcodeNo) ? prev.filter((b) => b !== barcodeNo) : [...prev, barcodeNo]
    );
  };

  const handleToggleAll = () => {
    const visibleIds = selectableRows.map((item) => item.BarcodeNo);
    if (allSelected) {
      setSelected((prev) => prev.filter((b) => !visibleIds.includes(b)));
    } else {
      setSelected((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  /* -------------------- Approve -------------------- */
  const handleApprove = async () => {
    setConfirmOpen(false);

    const employees = locationList
      .filter((item) => selected.includes(item.BarcodeNo))
      .map((item) => ({
        BarcodeNo: item.BarcodeNo,
        Date: item.Date || formatDate(selectedDate),
      }));

    if (employees.length === 0) return;

    const { successCount, failed } = await dispatch(updateLeavePriorList(employees, remarks));

    if (failed.length === 0) {
      setSnack({ open: true, severity: "success", message: `${successCount} employee(s) approved` });
    } else {
      setSnack({
        open: true,
        severity: successCount > 0 ? "warning" : "error",
        message: `${successCount} approved, ${failed.length} failed (${failed.join(", ")})`,
      });
    }

    loadList();
  };

  const approvedCount = locationList.filter(isApproved).length;
  const pendingCount = locationList.length - approvedCount;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#FFFFFF" }}>
        <HeaderBannerPage />

        <Box sx={{ maxWidth: "1000px", width: "100%", mx: "auto", p: 2, boxSizing: "border-box", fontFamily: "'Poppins', sans-serif" }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#0E3C66", textAlign: "center", mb: 2, letterSpacing: "1px" }}>
            Leave Approval
            {FILTER_BY_LOCATION
              ? isDivisionView
                ? ` - ${DIVISION_VIEW_DIVISION} Division`
                : loggedLocation
                ? ` - ${loggedLocation}`
                : ""
              : ""}
          </Typography>

          {/* ── Date | Counts ── */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" }, gap: 1.5, mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<CalendarTodayIcon />}
              onClick={() => setOpenDatePicker(true)}
              sx={{
                height: "52px",
                borderRadius: "20px",
                backgroundColor: "#142c54",
                fontWeight: "bold",
                textTransform: "none",
                fontSize: { xs: "13px", sm: "15px" },
                "&:hover": { backgroundColor: "#0D47A1" },
              }}
            >
              {formatDate(selectedDate)}
            </Button>
            <Box sx={countBoxSx("#fff4e0", "#f0c674", "#8a5a00")}>Pending: {pendingCount}</Box>
            <Box sx={{ ...countBoxSx("#e6f4ea", "#a8d5b5", "#1b5e20"), gridColumn: { xs: "1 / -1", sm: "auto" } }}>
              Approved: {approvedCount}
            </Box>
          </Box>

          {openDatePicker && (
            <DatePicker
              open={openDatePicker}
              value={selectedDate}
              onChange={(date) => {
                if (date) setSelectedDate(date);
                setOpenDatePicker(false);
              }}
              onClose={() => setOpenDatePicker(false)}
              slotProps={{ textField: { sx: { display: "none" } } }}
            />
          )}

          {/* ── Search | Remarks ── */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" }, gap: 1.5, mb: 1.5 }}>
            <TextField
              size="small"
              placeholder="Search service no, name, designation..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px", height: "48px" } }}
            />
            <TextField
              size="small"
              label="Remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px", height: "48px" } }}
            />
          </Box>

          {/* ── Select all bar ── */}
          <Box
            onClick={() => selectableRows.length > 0 && handleToggleAll()}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1,
              py: 0.5,
              mb: 1.5,
              borderRadius: "14px",
              backgroundColor: "#154c79",
              color: "#fff",
              cursor: selectableRows.length > 0 ? "pointer" : "default",
              userSelect: "none",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                disabled={selectableRows.length === 0}
                sx={{ color: "#fff", "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#fff" } }}
              />
              <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                Select all pending approval ({selectableRows.length})
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: "13px", pr: 1 }}>
              {selected.length} selected
            </Typography>
          </Box>

          {/* ── Status messages ── */}
          {leavePriorListLoading && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!leavePriorListLoading && leavePriorListError && (
            <Typography sx={{ textAlign: "center", py: 3, color: "#c62828" }}>{leavePriorListError}</Typography>
          )}
          {!leavePriorListLoading && !leavePriorListError && filteredList.length === 0 && (
            <Typography sx={{ textAlign: "center", py: 3, color: "#555" }}>No pending leave for this date</Typography>
          )}

          {/* ── Mobile: card list ── */}
          {!leavePriorListLoading && isMobile && filteredList.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {filteredList.map((item) => {
                const approved = isApproved(item);
                const checked = selected.includes(item.BarcodeNo);
                return (
                  <Paper
                    key={`${item.BarcodeNo}-${item.Date}`}
                    onClick={() => !approved && handleToggle(item.BarcodeNo)}
                    elevation={0}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1.25,
                      borderRadius: "14px",
                      border: checked ? "2px solid #2e7d32" : "1px solid #d6dce5",
                      backgroundColor: checked ? "#eef7f0" : approved ? "#f7f7f7" : "#fff",
                      cursor: approved ? "default" : "pointer",
                      userSelect: "none",
                      transition: "0.2s ease",
                    }}
                  >
                    <Checkbox checked={checked} disabled={approved} sx={{ p: 0.5 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#0E3C66", lineHeight: 1.3 }}>
                        {item.Name}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", color: "#555", mt: 0.25 }}>
                        {item.ServiceNo} · {item.Location}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", color: "#777" }}>{item.Designation}</Typography>
                    </Box>
                    {approved ? (
                      <Chip label="Approved" size="small" color="success" />
                    ) : (
                      <Chip label="Pending" size="small" sx={{ backgroundColor: "#fff4e0", color: "#8a5a00" }} />
                    )}
                  </Paper>
                );
              })}
            </Box>
          )}

          {/* ── Desktop / tablet: table ── */}
          {!leavePriorListLoading && !isMobile && filteredList.length > 0 && (
            <StyledTableContainer component={Paper}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCell padding="checkbox" />
                    <StyledTableCell>Service No</StyledTableCell>
                    <StyledTableCell>Name</StyledTableCell>
                    <StyledTableCell>Location</StyledTableCell>
                    <StyledTableCell>Designation</StyledTableCell>
                    <StyledTableCell>Status</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredList.map((item) => {
                    const approved = isApproved(item);
                    const checked = selected.includes(item.BarcodeNo);
                    return (
                      <StyledTableRow
                        key={`${item.BarcodeNo}-${item.Date}`}
                        onClick={() => !approved && handleToggle(item.BarcodeNo)}
                        selected={checked}
                      >
                        <StyledTableCell padding="checkbox">
                          <Checkbox checked={checked} disabled={approved} />
                        </StyledTableCell>
                        <StyledTableCell>{item.ServiceNo}</StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "left" }}>{item.Name}</StyledTableCell>
                        <StyledTableCell>{item.Location}</StyledTableCell>
                        <StyledTableCell>{item.Designation}</StyledTableCell>
                        <StyledTableCell>
                          {approved ? (
                            <Chip label="Approved" size="small" color="success" />
                          ) : (
                            <Chip label="Pending" size="small" sx={{ backgroundColor: "#fff4e0", color: "#8a5a00" }} />
                          )}
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}

          {/* ── Sticky Approve button ── */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              mt: 2,
              py: 1.5,
              backgroundColor: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(4px)",
              zIndex: 20,
            }}
          >
            <Button
              fullWidth
              variant="contained"
              startIcon={updateLoading ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
              disabled={selected.length === 0 || updateLoading}
              onClick={() => setConfirmOpen(true)}
              sx={{
                height: "52px",
                borderRadius: "14px",
                backgroundColor: "#2e7d32",
                fontWeight: "bold",
                fontSize: "16px",
                textTransform: "none",
                "&:hover": { backgroundColor: "#1b5e20" },
                "&:disabled": { backgroundColor: "#cccccc", color: "#666666" },
              }}
            >
              Approve ({selected.length})
            </Button>
          </Box>
        </Box>

        {/* Confirm dialog */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 700, color: "#1e3c72" }}>Approve Leave?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Approve leave for {selected.length} employee(s) on {formatDate(selectedDate)}?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmOpen(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: "none" }}>
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              variant="contained"
              sx={{ borderRadius: 2, textTransform: "none", backgroundColor: "#2e7d32", "&:hover": { backgroundColor: "#1b5e20" } }}
            >
              Yes, Approve
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snack.open}
          autoHideDuration={5000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
            {snack.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

const countBoxSx = (bg, border, color) => ({
  background: bg,
  border: `1px solid ${border}`,
  borderRadius: "20px",
  height: "52px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 700,
  fontSize: { xs: "13px", sm: "15px" },
  color,
});

export default LeaveApproval;